/**
 * Feature gating — which parts of the app are visible for an org.
 *
 * Drives the menus, dashboard tiles, document library, tick lists, etc.
 *
 * The visibility rules combine three things:
 *   1. The role-based default (runs_jobs / works_for_others / both)
 *   2. Explicit `disabled_features[]` on the org (user turned it off)
 *   3. Explicit `enabled_features[]` on the org (user turned it on, overrides default-off)
 *
 * If the org has not completed the profile picker yet, we treat them as
 * "show everything" so existing accounts keep working untouched.
 */

import type { OrgMembership, OrgRoleValue } from './org';
export type { OrgRoleValue };

export type FeatureKey =
  | 'cpp'             // Construction Phase Plan
  | 'f10'             // HSE F10 notification
  | 'pc_site_walk'    // Weekly Principal-Contractor walk tick list
  | 'permits'         // Hot works / confined space / dig / height permits
  | 'subbies'         // Sub-contractor register
  | 'plant'           // Owned plant / tools register (LOLER, PAT)
  | 'training_matrix' // CSCS / First Aid / cards
  | 'coshh'           // COSHH chemical register
  | 'rams'            // RAMS library
  | 'sign_in'         // Site sign-in / sign-out
  | 'daily_check'     // Daily site walk tick list
  | 'accidents'       // Accident book / RIDDOR
  | 'documents';      // Document library (policies, CDM, certificates)

export interface FeatureMeta {
  key: FeatureKey;
  name: string;
  description: string;
  /**
   * 'always' — every customer needs this, never hide it.
   * 'pc_only' — only relevant if you run the job (CPP, F10, weekly PC walk).
   * 'optional' — useful but easy to live without (e.g. subbies for a solo sparky).
   */
  category: 'always' | 'pc_only' | 'optional';
}

export const FEATURES: FeatureMeta[] = [
  { key: 'sign_in',         name: 'Site sign-in',                              description: 'Track who is on site each day. Required on every job.',                                       category: 'always' },
  { key: 'rams',            name: 'RAMS library',                              description: 'Risk Assessments and Method Statements for the work you do.',                                  category: 'always' },
  { key: 'coshh',           name: 'COSHH register',                            description: 'List of chemicals on site and their hazards.',                                                 category: 'always' },
  { key: 'accidents',       name: 'Accident book',                             description: 'Log accidents, near-misses and RIDDOR-reportable events.',                                     category: 'always' },
  { key: 'documents',       name: 'Document library',                          description: 'H&S policies, certificates, CDM templates.',                                                   category: 'always' },
  { key: 'daily_check',     name: 'Daily check',                               description: 'End-of-day tick list — work area tidy, tools secured, etc.',                                   category: 'always' },

  { key: 'cpp',             name: 'CPP (Construction Phase Plan)',             description: 'Required by CDM 2015 when you run a job and there is more than one contractor on site.',        category: 'pc_only' },
  { key: 'f10',             name: 'F10 — HSE project notification',            description: 'Notify HSE when a job exceeds 30 days OR 500 person-days. Principal-Contractor responsibility.', category: 'pc_only' },
  { key: 'pc_site_walk',    name: 'Weekly site walk',                          description: 'Weekly walk-around recorded by the person in charge of the site.',                             category: 'pc_only' },

  { key: 'permits',         name: 'Permits to work',                           description: 'Hot works, confined space, working at height, excavation permits.',                            category: 'optional' },
  { key: 'subbies',         name: 'Sub-contractor register',                   description: 'Track sub-contractors, their insurance and competence.',                                       category: 'optional' },
  { key: 'plant',           name: 'Plant register',                            description: 'Owned plant + tools, LOLER and PAT dates.',                                                    category: 'optional' },
  { key: 'training_matrix', name: 'Training matrix',                           description: 'CSCS, First Aid and other cards by person, with expiry dates.',                                category: 'optional' },
];

export const FEATURE_BY_KEY: Record<FeatureKey, FeatureMeta> =
  FEATURES.reduce((acc, f) => { acc[f.key] = f; return acc; }, {} as Record<FeatureKey, FeatureMeta>);

/**
 * Default features per role. Anything not listed here is HIDDEN by default
 * for that role (but still reachable via the "Need something not here?" flow).
 */
export const ROLE_DEFAULT_VISIBLE: Record<OrgRoleValue, FeatureKey[]> = {
  runs_jobs: [
    'sign_in','rams','coshh','accidents','documents','daily_check',
    'cpp','f10','pc_site_walk',
    'permits','subbies','plant','training_matrix',
  ],
  works_for_others: [
    'sign_in','rams','coshh','accidents','documents','daily_check',
    'training_matrix',
    // CPP / F10 / pc_site_walk hidden — they're not the PC.
    // permits / subbies / plant hidden by default — easy to add if needed.
  ],
  both: [
    'sign_in','rams','coshh','accidents','documents','daily_check',
    'cpp','f10','pc_site_walk',
    'permits','subbies','plant','training_matrix',
  ],
};

/**
 * Decide whether a feature is currently visible for this org.
 *
 *   - Until the profile picker is completed: show everything
 *     (so existing test accounts don't suddenly lose features).
 *   - Once role is set: use the role default + per-org overrides.
 */
export function isFeatureEnabled(
  org: OrgMembership | null | undefined,
  key: FeatureKey,
): boolean {
  if (!org) return true;
  // Always-on features can never be disabled.
  if (FEATURE_BY_KEY[key]?.category === 'always') return true;

  // Per-org overrides win.
  const enabled = org.enabled_features ?? [];
  const disabled = org.disabled_features ?? [];
  if (disabled.includes(key)) return false;
  if (enabled.includes(key))  return true;

  // No org-level role set yet → show everything (safety net for existing
  // accounts that pre-date the profile picker).
  if (!org.org_role || !org.profile_completed) return true;

  const visible = ROLE_DEFAULT_VISIBLE[org.org_role];
  // Defensive: if the stored value is somehow outside the union, show everything.
  if (!visible) return true;
  return visible.includes(key);
}

/**
 * The list of features currently HIDDEN for this org. Used by the
 * "Need something not here? Show all H&S tools" flow.
 */
export function listHiddenFeatures(org: OrgMembership | null | undefined): FeatureMeta[] {
  if (!org || !org.org_role || !org.profile_completed) return [];
  return FEATURES.filter((f) => f.category !== 'always' && !isFeatureEnabled(org, f.key));
}

// ---------- Trade catalogue ---------------------------------------------------

export const TRADES = [
  { key: 'electrical',   label: 'Electrical' },
  { key: 'plumbing',     label: 'Plumbing & heating' },
  { key: 'carpentry',    label: 'Carpentry / kitchens / fit-out' },
  { key: 'bricklaying',  label: 'Bricklaying / general building' },
  { key: 'groundworks',  label: 'Groundworks' },
  { key: 'roofing',      label: 'Roofing' },
  { key: 'plastering',   label: 'Plastering' },
  { key: 'decorating',   label: 'Decorating / painting' },
  { key: 'landscaping',  label: 'Landscaping / externals' },
  { key: 'mixed',        label: 'Mixed builder (a bit of everything)' },
  { key: 'other',        label: 'Other' },
] as const;

export type TradeKey = typeof TRADES[number]['key'];

// ---------- Risky activities catalogue ----------------------------------------

export const RISKY_ACTIVITIES = [
  { key: 'height',          label: 'Working above 2 m off the ground (ladders, roofs, scaffolds, MEWPs)' },
  { key: 'excavation',      label: 'Excavation / digging holes' },
  { key: 'confined_spaces', label: 'Confined spaces (manholes, lift pits, tanks)' },
  { key: 'hot_works',       label: 'Hot works (welding, cutting, grinding)' },
  { key: 'asbestos',        label: 'Working on pre-2000 buildings (asbestos likely)' },
  { key: 'lifting',         label: 'Lifting operations (crane, telehandler, hoist)' },
] as const;

export type RiskyActivityKey = typeof RISKY_ACTIVITIES[number]['key'];

// ---------- Profile-trade → RAMS-template-trade mapping --------------------

/**
 * Map our profile trade keys (chosen during the wizard) to the trade strings
 * used to tag RAMS templates. Used to surface "Suggested for your trade"
 * RAMS without forcing the user to scroll through 100+ templates.
 */
export const PROFILE_TRADE_TO_RAMS_TRADES: Record<string, string[]> = {
  electrical:   ['Electrical'],
  plumbing:     ['Plumbing'],
  carpentry:    ['Carpentry & joinery'],
  bricklaying:  ['Bricklaying'],
  groundworks:  ['Groundworks', 'Excavation', 'Foundations & concrete'],
  roofing:      ['Roofing'],
  plastering:   ['Drylining & plastering'],
  decorating:   ['Decorating'],
  // Mixed builder pulls from the building-trade set
  mixed:        ['Bricklaying','Carpentry & joinery','Drylining & plastering','Decorating','Roofing','Groundworks'],
  // landscaping / other → nothing pre-tagged
};

/**
 * Default "first RAMS" template ID per profile trade. Used by the
 * onboarding First-RAMS flow to pre-pick a template so the user can
 * generate their first branded RAMS in ~30 seconds without scrolling
 * a 100-template picker. These map to RAMS_TEMPLATES ids in
 * data/ramsTemplates.ts.
 */
export const PROFILE_TRADE_TO_DEFAULT_RAMS_TEMPLATE: Record<string, string> = {
  electrical:   'electrical_first_fix',
  plumbing:     'plumbing_first_fix',
  carpentry:    'carpentry_first_fix',
  bricklaying:  'brickwork',
  groundworks:  'excavation_foundations',
  roofing:      'roof_tiling',
  plastering:   'drylining_plastering',
  decorating:   'decorating',
  // Mixed builder gets brickwork — the most universally relatable
  // "Sunday-night RAMS" template for a general builder.
  mixed:        'brickwork',
  // landscaping / other fall through to the fallback below.
};
/** Fallback when no trade-specific default is set. */
export const FALLBACK_FIRST_RAMS_TEMPLATE = 'site_setup';

/**
 * Map risky activities to RAMS template trade strings that cover them.
 */
export const ACTIVITY_TO_RAMS_TRADES: Record<string, string[]> = {
  height:          ['Roofing', 'Scaffolding'],
  excavation:      ['Excavation', 'Groundworks'],
  lifting:         ['Telehandler / lift operations', 'Concrete pump', 'Steel erection'],
  // confined_spaces / hot_works / asbestos → no direct trade match in v1
};

/**
 * Returns the RAMS-template trade strings to highlight as "for you" given
 * an org's profile. Combines trades + risky activities, deduped.
 */
export function ramsTradesForOrg(
  trades: string[] | null | undefined,
  riskyActivities: string[] | null | undefined,
): string[] {
  const out = new Set<string>();
  for (const k of trades ?? [])           for (const t of (PROFILE_TRADE_TO_RAMS_TRADES[k] ?? [])) out.add(t);
  for (const k of riskyActivities ?? [])  for (const t of (ACTIVITY_TO_RAMS_TRADES[k] ?? []))      out.add(t);
  return [...out];
}
